import { captureReferralCode } from "@/shared/utils/referral-storage";
import { AgenciesService } from "@services/requests";
import { FF_APP, FF_EARLY_ACCESS } from "@/shared/utils/feature-flag";

/**
 * Parses a stored return URL into its pathname and search parts, capturing any
 * referral code found in the query string. Returns empty parts when the value
 * is absent or not a valid URL.
 *
 * @param savedReturnPath - The absolute return URL persisted before login.
 * @returns The extracted pathname and search string.
 */
export function parseReturnPathFromStorage(savedReturnPath: string | null): { pathname: string; search: string } {
  if (!savedReturnPath) return { pathname: "", search: "" };
  try {
    const url = new URL(savedReturnPath);
    captureReferralCode(url.search);
    return { pathname: url.pathname, search: url.search };
  } catch {
    return { pathname: "", search: "" };
  }
}

/**
 * Builds the early-access route, preserving the incoming query string.
 *
 * @param search - Query string to carry over (may be empty).
 * @returns The early-access path with the search appended.
 */
/**
 * Builds the early-access route, preserving any query string so referral and
 * campaign parameters survive the redirect.
 *
 * @param search - The query string to append (may be empty).
 * @returns The early-access path with search preserved.
 */
function earlyAccessPath(search: string): string {
  return "/early-access" + (search || "");
}

/**
 * Checks whether the authenticated user belongs to at least one agency,
 * treating any lookup failure as "no agencies".
 *
 * @returns True when the user has one or more agencies.
 */
/**
 * Checks whether the authenticated user belongs to at least one agency,
 * treating any lookup failure as "no agencies" so callers fall back safely.
 *
 * @returns True when the user has one or more agencies.
 */
async function hasAgencies(): Promise<boolean> {
  try {
    const agencies = await AgenciesService.getAllAgencies();
    return Boolean(agencies && agencies.length > 0);
  } catch {
    return false;
  }
}

/**
 * Resolves the landing route for an app user: the workspace when they already
 * have an agency, otherwise the early-access flow (when enabled) or workspace.
 *
 * @param search - Query string to preserve on redirect.
 * @returns The destination path for the authenticated app user.
 */
/**
 * Resolves the landing route for an app-enabled user: the workspace when they
 * already have agencies, otherwise early access (when enabled) to complete
 * onboarding.
 *
 * @param search - The query string to carry into the early-access redirect.
 * @returns The path to navigate the app user to after login.
 */
export async function resolveForAppUser(search: string): Promise<string> {
  if (await hasAgencies()) return "/app/workspace";
  return FF_EARLY_ACCESS ? earlyAccessPath(search) : "/app/workspace";
}

/**
 * Determines where to send the user after a successful OAuth callback: honors a
 * saved in-app return path when app routes are enabled, otherwise falls back to
 * the app-user resolution or the early-access/home route per feature flags.
 *
 * @param savedReturnPath - The absolute return URL persisted before login.
 * @returns The final post-authentication destination path.
 */
/**
 * Determines where to send the user after a successful login: honors a saved
 * in-app return path when the app is enabled, otherwise resolves by agency
 * membership or falls back to early access / the public home per feature flags.
 *
 * @param savedReturnPath - The URL persisted before the login redirect, if any.
 * @returns The destination path to navigate to post-authentication.
 */
export async function resolvePostAuthDestination(savedReturnPath: string | null): Promise<string> {
  const { pathname, search } = parseReturnPathFromStorage(savedReturnPath);

  if (pathname.startsWith("/app/") && FF_APP) return pathname + search;
  if (FF_APP) return resolveForAppUser(search);
  return FF_EARLY_ACCESS ? earlyAccessPath(search) : "/";
}
